import { cn } from '@/lib/utils'
import { CATEGORY_MAP, type Category } from '@/lib/constants'
import { Check, Loader2 } from 'lucide-react'

interface CategoryButtonProps {
  category: Category
  isActive: boolean
  isCompleted: boolean
  disabled?: boolean
  elapsedDisplay?: string
  onClick: () => void
}

export function CategoryButton({ category, isActive, isCompleted, disabled, elapsedDisplay, onClick }: CategoryButtonProps) {
  const config = CATEGORY_MAP[category]
  const Icon = config.icon

  if (isActive) {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'relative flex flex-col items-center justify-center gap-2 rounded-2xl p-4 min-h-[100px] transition-all duration-300',
          'bg-red-500 text-white shadow-lg shadow-red-500/30 scale-[1.02]',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className="absolute inset-0 rounded-2xl animate-pulse-slow bg-red-500/80" />
        <div className="relative z-10 flex flex-col items-center gap-2">
          <Icon className="h-6 w-6" />
          <span className="text-sm font-medium">{config.label}</span>
          <span className="text-lg font-bold tabular-nums tracking-wider">{elapsedDisplay || '00:00'}</span>
        </div>
      </button>
    )
  }

  if (isCompleted) {
    return (
      <button
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={cn(
          'relative flex flex-col items-center justify-center gap-2 rounded-2xl p-4 min-h-[100px] transition-all duration-300',
          'bg-green-500 text-white shadow-lg shadow-green-500/30',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <Check className="h-6 w-6" />
        <span className="text-sm font-medium">{config.label}</span>
        <span className="text-xs opacity-80">已完成</span>
      </button>
    )
  }

  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-2xl p-4 min-h-[100px] transition-all duration-200',
        'bg-gradient-to-br text-white shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]',
        config.gradient,
        disabled && 'opacity-50 cursor-not-allowed grayscale hover:scale-100 hover:shadow-md'
      )}
    >
      <Icon className="h-6 w-6" />
      <span className="text-sm font-medium">{config.label}</span>
    </button>
  )
}
